// Copyright (c) 2025 Audere, All Rights Reserved

import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { PortalApiResult } from "../../types/auth";
import { apiSlice } from "../api/apiSlice";
import type {
  CreateProjectPayload,
  GetProjectData,
  GetProjectPayload,
  ListProjectsData,
  ListRDTsData,
  addRDTPayload,
  ProjectMutationResponse,
  UpdateProjectPayload,
  uploadRdtExamplePhotoResponse,
  uploadRdtExamplePhotoPayload,
  addRDTResponse,
  ReportCardResponse,
  ReportCardPayload,
  CreatePhotosetPayload,
  CreatePhotosetResponse,
  ListPhotosetsPayload,
  ListPhotosetsResponse,
  FileUploadApiStatusType,
  UpdateRDTPayload,
  UpdateRDTResponse,
  TestData,
  ListApplicationsResponse,
  ListApplicationsPayload,
  ListAppBuildsResponse,
  ListAppBuildsPayload,
  DownloadSdkPayload,
  DownloadSdkResponse,
  CreateApplicationPayload,
  CreateApplicationResponse,
  BuildApplicationResponse,
  BuildApplicationPayload,
} from "../../types/projects";
import { store } from "../store";

const apiSliceWithTagTypes = apiSlice.enhanceEndpoints({
  addTagTypes: ["ProjectDetails", "ListProjects", "DashboardProjectDetails"],
});

export const projectApiSlice = apiSliceWithTagTypes.injectEndpoints({
  endpoints: builder => ({
    listRDTs: builder.query<ListRDTsData, void>({
      query: () => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "list_rdts",
          input: {
            organization_uid: store.getState().auth.organizationId,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<ListRDTsData>) => {
        return rawResult.output;
      },
    }),
    createProject: builder.mutation<ProjectMutationResponse, CreateProjectPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "create_project",
          input: {
            organization_uid: store.getState().auth.organizationId,
            name: args.projectName,
            rdts: args.rdts,
            model_type: args.modelType,
          },
        }),
      }),
      invalidatesTags: response =>
        response
          ? [
              { type: "ListProjects", id: "LIST" },
              { type: "ProjectDetails", id: response.project_uid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<ProjectMutationResponse>) => {
        return rawResult.output;
      },
      transformErrorResponse: (response: FetchBaseQueryError) => {
        return response;
      },
    }),
    updateProject: builder.mutation<ProjectMutationResponse, UpdateProjectPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "update_project",
          input: {
            organization_uid: store.getState().auth.organizationId,
            project_uid: args.projectUid,
            name: args.projectName,
            rdts: args.rdts,
            model_type: args.modelType,
          },
        }),
      }),
      invalidatesTags: response =>
        response
          ? [
              { type: "ListProjects", id: "LIST" },
              { type: "ProjectDetails", id: response.project_uid },
            ]
          : [],
      transformResponse: (rawResult: PortalApiResult<ProjectMutationResponse>) => {
        return rawResult.output;
      },
      transformErrorResponse: (response: FetchBaseQueryError) => {
        return response;
      },
    }),
    addRDT: builder.mutation<addRDTResponse, addRDTPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "add_rdt",
          input: {
            organization_uid: store.getState().auth.organizationId,
            brand: args.brand,
            model: args.model,
            condition: args.condition,
            who_product_name: args.who_product_name,
            who_pq: args.who_pq,
            results: args.results,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<{ rdt_uid: string }>) => {
        return rawResult.output;
      },
    }),
    listProjects: builder.query<ListProjectsData, void>({
      query: () => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "list_projects",
          input: {
            organization_uid: store.getState().auth.organizationId,
          },
        }),
      }),
      providesTags: response => {
        return response ? [{ type: "ListProjects", id: "LIST" }] : [];
      },
      transformResponse: (rawResult: PortalApiResult<ListProjectsData>) => {
        return rawResult.output;
      },
    }),
    getProject: builder.query<GetProjectData, GetProjectPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_project",
          input: {
            organization_uid: store.getState().auth.organizationId,
            project_uid: args.projectUid,
          },
        }),
      }),
      providesTags: response => {
        return response
          ? [
              { type: "ProjectDetails", id: response.project.project_uid },
              { type: "DashboardProjectDetails", id: "GET_PROJECT_DETAILS" },
            ]
          : [];
      },
      transformResponse: (rawResult: PortalApiResult<GetProjectData>) => {
        return rawResult.output;
      },
    }),
    uploadRdtExamplePhoto: builder.mutation<
      uploadRdtExamplePhotoResponse,
      uploadRdtExamplePhotoPayload
    >({
      query: ({ rdt_uid }) => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "update_example_photo_for_rdt",
          input: {
            organization_uid: store.getState().auth.organizationId,
            rdt_uid,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<{ upload_url: string }>) => {
        return rawResult.output;
      },
    }),
    startLabeling: builder.mutation<{ upload_url: string }, { rdt_uid: string }>({
      query: ({ rdt_uid }) => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "start_labeling",
          input: {
            organization_uid: store.getState().auth.organizationId,
            rdt_uid,
          },
        }),
      }),
      invalidatesTags: () => [{ type: "DashboardProjectDetails", id: "GET_PROJECT_DETAILS" }],
      transformResponse: (rawResult: PortalApiResult<{ upload_url: string }>) => {
        return rawResult.output;
      },
    }),

    getReportCard: builder.query<ReportCardResponse, ReportCardPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_report_card",
          input: args,
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<ReportCardResponse>) => {
        return rawResult.output;
      },
    }),

    getPhotoUrlForUpload: builder.query<
      { status: FileUploadApiStatusType; upload_url?: string },
      { photosetUid: string; hash: string }
    >({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_photo_url_for_upload",
          input: {
            organization_uid: store.getState().auth.organizationId,
            photoset_uid: args.photosetUid,
            photo_sha256_hash: args.hash,
          },
        }),
      }),
      transformResponse: (
        rawResult: PortalApiResult<{ status: FileUploadApiStatusType; upload_url?: string }>
      ) => {
        return rawResult.output;
      },
    }),
    createPhotoset: builder.mutation<CreatePhotosetResponse, CreatePhotosetPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "create_photoset",
          input: {
            organization_uid: store.getState().auth.organizationId,
            project_uid: args.projectUid,
            rdt_uid: args.rdtUid,
            purpose: args.purpose,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<CreatePhotosetResponse>) => {
        return rawResult.output;
      },
    }),
    listPhotosets: builder.query<ListPhotosetsResponse, ListPhotosetsPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "list_photosets",
          input: {
            organization_uid: store.getState().auth.organizationId,
            project_uid: args.projectUid,
            rdt_uid: args.rdtUid,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<ListPhotosetsResponse>) => {
        return rawResult.output;
      },
    }),
    updateRDT: builder.mutation<UpdateRDTResponse, UpdateRDTPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "update_rdt",
          input: {
            organization_uid: store.getState().auth.organizationId,
            rdt_uid: args.rdt_uid,
            allow_sharing: args.allow_sharing ?? false,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<{ rdt_uid: string }>) => {
        return rawResult.output;
      },
    }),
    getRdt: builder.query<TestData, { rdtUid: string }>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: {
          method_id: "get_rdt",
          input: {
            organization_uid: store.getState().auth.organizationId,
            rdt_uid: args.rdtUid,
          },
        },
      }),
      transformResponse: (rawResult: PortalApiResult<{ rdt: TestData }>) => {
        return rawResult.output.rdt;
      },
    }),
    listApplications: builder.query<ListApplicationsResponse, ListApplicationsPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "list_applications",
          input: {
            organization_uid: store.getState().auth.organizationId,
            project_uid: args.projectUid,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<ListApplicationsResponse>) => {
        return rawResult.output;
      },
    }),
    listApplicationBuilds: builder.query<ListAppBuildsResponse, ListAppBuildsPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "list_app_builds",
          input: {
            organization_uid: store.getState().auth.organizationId,
            application_uid: args.applicationUid,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<ListAppBuildsResponse>) => {
        return rawResult.output;
      },
    }),
    downloadSdk: builder.mutation<DownloadSdkResponse, DownloadSdkPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "download_sdk",
          input: {
            organization_uid: store.getState().auth.organizationId,
            app_build_uid: args.buildUid,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<DownloadSdkResponse>) => {
        return rawResult.output;
      },
    }),
    createApplication: builder.mutation<CreateApplicationResponse, CreateApplicationPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "create_application",
          input: {
            organization_uid: store.getState().auth.organizationId,
            project_uid: args.projectUid,
            name: args.appName,
            android_app_id: args.appId,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<CreateApplicationResponse>) => {
        return rawResult.output;
      },
    }),
    buildApplication: builder.mutation<BuildApplicationResponse, BuildApplicationPayload>({
      query: args => ({
        url: "/fig",
        method: "POST",
        body: JSON.stringify({
          method_id: "build_application",
          input: {
            organization_uid: store.getState().auth.organizationId,
            application_uid: args.applicationUid,
          },
        }),
      }),
      transformResponse: (rawResult: PortalApiResult<BuildApplicationResponse>) => {
        return rawResult.output;
      },
    }),
  }),
});

export const {
  useListRDTsQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useAddRDTMutation,
  useListProjectsQuery,
  useLazyGetProjectQuery,
  useUploadRdtExamplePhotoMutation,
  useStartLabelingMutation,
  useLazyGetReportCardQuery,
  useLazyGetPhotoUrlForUploadQuery,
  useCreatePhotosetMutation,
  useLazyListPhotosetsQuery,
  useUpdateRDTMutation,
  useLazyGetRdtQuery,
  useListApplicationsQuery,
  useLazyListApplicationBuildsQuery,
  useDownloadSdkMutation,
  useCreateApplicationMutation,
  useBuildApplicationMutation,
} = projectApiSlice;
